import { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

import './MuseumLabelModal.css';

function getFocusableElements(container) {
  if (!container) {
    return [];
  }

  const selectors = [
    'button',
    'a[href]',
    'area[href]',
    'input:not([type="hidden"])',
    'select',
    'textarea',
    '[tabindex]:not([tabindex="-1"])',
  ];

  return Array.from(container.querySelectorAll(selectors.join(',')))
    .filter((element) => !element.hasAttribute('disabled') && !element.getAttribute('aria-hidden'));
}

export default function MuseumLabelModal({ isOpen, onClose, labelData }) {
  const dialogRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);

  const safeLabel = useMemo(() => (labelData ? {
    ...labelData,
  } : null), [labelData]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    previouslyFocusedElementRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusable = getFocusableElements(dialogRef.current);
      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === first || !dialogRef.current.contains(document.activeElement)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }

      if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    const body = document.body;
    const originalOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    const focusTimer = window.setTimeout(() => {
      const focusable = getFocusableElements(dialogRef.current);
      if (focusable.length > 0) {
        focusable[0].focus();
        return;
      }

      if (dialogRef.current && typeof dialogRef.current.focus === 'function') {
        dialogRef.current.focus();
      }
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      body.style.overflow = originalOverflow;

      if (previouslyFocusedElementRef.current && typeof previouslyFocusedElementRef.current.focus === 'function') {
        previouslyFocusedElementRef.current.focus();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen || !safeLabel) {
    return null;
  }

  const handleBackdropMouseDown = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const metaLines = [
    safeLabel.medium,
    safeLabel.size,
    safeLabel.edition,
    safeLabel.extras,
  ].filter(Boolean);

  return createPortal(
    (
      <div
        className="museum-label-modal__backdrop"
        role="presentation"
        onMouseDown={handleBackdropMouseDown}
      >
        <div
          className="museum-label-modal__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="museum-label-modal-title"
          ref={dialogRef}
          tabIndex={-1}
        >
          <button
            type="button"
            className="museum-label-modal__close-button"
            onClick={onClose}
            aria-label="Close"
          >
            Close
          </button>

          <div className="museum-label-modal__content">
            <div className="museum-label-card sheen">
              <h2 id="museum-label-modal-title" className="museum-label-card__title">
                {safeLabel.title}
              </h2>
              {safeLabel.artist ? (
                <p className="museum-label-card__artist">{safeLabel.artist}</p>
              ) : null}
              {safeLabel.year ? (
                <p className="museum-label-card__meta">{safeLabel.year}</p>
              ) : null}

              <div className="museum-label-card__details">
                {metaLines.map((line, index) => (
                  <p key={`${line}-${index}`} className="museum-label-card__meta">
                    {line}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
    document.body
  );
}
